import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core'
import { Deliverable, DeliveryStatus } from '@app/features/scoping/models/deliverable.model'
import { formatMonetaryValue, trackById } from '@shared/utils/utils'
import { ScopeElementsConfig, UpdateFeeItemPayload, UpdateRolePayload, UpdateTpcPayload } from '../models'

import { FormControl } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { ApplicationSetting } from '@app/core/model/enums/application-setting.enum'
import { Privilege } from '@app/core/model/enums/privilege.enum'
import { ThirdPartyCost } from '@app/core/model/third-party-cost.model'
import { User } from '@app/core/model/user.model'
import { AuthService } from '@app/core/service/auth.service'
import { LanguageService } from '@app/core/service/language.service'
import { Money } from '@app/features/scope-overview/model/money.model'
import { ApprovalFlowService } from '@app/features/scope-overview/service/approval-flow.service'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { ScopeUiModalComponent } from '@app/shared/components/ui-components/scope-ui-modal/scope-ui-modal.component'
import { isPreferenceActive } from '@app/shared/components/ui-components/scope-ui-table/table-column-preferences.type'
import { MenuOptions } from '@core/model/definitions/menu-options.interface'
import { plainToInstance } from 'class-transformer'
import { isNil } from 'lodash'
import { DescriptionUpdate } from '../../description-editor/description-editor.component'
import { UpdateComponentPayload } from '../models/update-component-payload.interface'
import { ChangeItem } from '@app/features/scope-overview/change-request/model'
import { ScopeElementsService } from '../services/scope-elements.service'
import { getThirdPartyCostColumns } from '../utils'
import { getCrFieldToDisplay, isChangeItemAdded, isChangeItemRemoved, showCrDifferences } from '../utils/change-request'
import { FormatHoursPipe } from '@app/shared/pipe/formatHours.pipe'
import { ComponentWithChanges } from '../models/entities-with-changes/component-with-changes.interface'
import { ConvertCurrencyPipe } from '@app/shared/pipe/convertCurrency.pipe'
import { DEFAULT_PLACEHOLDER } from '@app/shared/constants/default-placeholder.const'
import { formatPercentage } from '../utils/format-percentage.utils'

@Component({
  selector: 'scope-components-row',
  templateUrl: './components-row.component.html',
  styleUrls: ['./components-row.component.scss'],
  providers: [FormatHoursPipe],
})
export class ComponentsRowComponent {
  @Input() component: ComponentWithChanges
  @Input() config: ScopeElementsConfig
  @Input() deliverable: Deliverable
  @Input() componentMenuOptions: MenuOptions[]
  @Input() feeMenuOptions: MenuOptions[]
  @Input() level: number
  @Input() changeItem: ChangeItem

  @Output() onUpdateComponent = new EventEmitter<UpdateComponentPayload>()
  @Output() onOverrideQuantity = new EventEmitter<ScopeComponent>()
  @Output() onOverrideRateCardRoleHours = new EventEmitter<UpdateRolePayload>()
  @Output() onUpdateComponentFee = new EventEmitter<UpdateFeeItemPayload>()
  @Output() onUpdateRole = new EventEmitter<UpdateRolePayload>()
  @Output() onUpdateComponentTpc = new EventEmitter<UpdateTpcPayload>()
  @Output() onReorderComponentTpcs = new EventEmitter<{
    component: ScopeComponent
    thirdPartyCosts: ThirdPartyCost[]
  }>()

  get currentUser(): User {
    return this.authService.loggedInUser
  }

  get isAgencyPriceEditable(): boolean {
    return this.config?.isEditable && this.component.fixedPricing && this.component.fixedFeeEditable
  }

  get updatedConfig(): ScopeElementsConfig {
    return { ...this.config, isEditable: this.config?.isDeliverableEditable, changeItem: this.changeItem }
  }

  isEditAgencyPrice = false
  agencyPriceControl = new FormControl()

  constructor(
    protected authService: AuthService,
    protected scopeElementsService: ScopeElementsService,
    private dialog: MatDialog,
    private lang: LanguageService,
    private scopeApprovalFlowService: ApprovalFlowService,
    private formatHoursPipe: FormatHoursPipe,
    private convertCurrencyPipe: ConvertCurrencyPipe
  ) {
    this.scopeElementsService.setFeeColumns()
  }

  getTpcColumns = () =>
    getThirdPartyCostColumns(this.config?.isEditable, (thirdPartyCost: ThirdPartyCost) =>
      this.onUpdateComponentTpc.emit({ deliverable: this.deliverable, component: this.component, thirdPartyCost })
    )

  updateComponentTpc(thirdPartyCost: ThirdPartyCost) {
    this.onUpdateComponentTpc.emit({ deliverable: this.deliverable, component: this.component, thirdPartyCost })
  }

  reorderComponentTpcs(thirdPartyCosts: ThirdPartyCost[]) {
    this.onReorderComponentTpcs.emit({ component: this.component, thirdPartyCosts })
  }

  selectComponent() {
    this.scopeElementsService.toggleComponent(this.component.id)
  }

  updateComponent(updatedFields: Partial<ScopeComponent>) {
    const changes = Object.keys(updatedFields).reduce((acc, key) => {
      const change = this.changeItem?.data?.[key]
      acc[key] = {
        old: !isNil(change) ? change.old : this.component[key],
        new: updatedFields[key],
      }
      return acc
    }, {})

    this.onUpdateComponent.emit({
      originalComponent: this.component,
      component: plainToInstance(ScopeComponent, { ...this.component, ...updatedFields }),
      changes,
    })
  }

  saveComponentExtraDescription($event: DescriptionUpdate) {
    this.updateComponent({ description: $event.main, extraDescription: $event.extra })
  }

  updateAgencyPrice(amount: number) {
    this.isEditAgencyPrice = false

    let fixedCost = !isNil(amount) ? new Money(amount, this.config?.currency) : null

    if (fixedCost && this.component.quantity > 1) {
      const eachValue = fixedCost.divide(this.component.quantity)
      const allValue = fixedCost.multiply(this.component.quantity)

      const dialog = this.dialog.open(ScopeUiModalComponent, {
        data: {
          title: `Fixed Fee for all or each?`,
          body: `The quantity is more than one for ${
            this.component.name
          }. Is the fixed fee you are setting for ALL ${this.lang.get('component.p')} or EACH ${this.lang.get(
            'component'
          )}`,
          confirmText: `Each - Set total at ${formatMonetaryValue(allValue)}  (${formatMonetaryValue(fixedCost)} each)`,
          confirmCallback: () => {
            this.updateComponent({ fixedCost })
            dialog.close()
          },
          hasCancelButton: true,
          cancelText: `All - Set total at ${formatMonetaryValue(fixedCost)} (${formatMonetaryValue(eachValue)} each)`,
          cancelCallback: () => {
            fixedCost = eachValue
            this.updateComponent({ fixedCost })
            dialog.close()
          },
          flatButton: true,
        },
      })
    } else {
      this.updateComponent({ fixedCost })
    }
  }

  overrideQuantity() {
    return () => this.onOverrideQuantity.emit(this.component)
  }

  editAgencyPrice($event: MouseEvent) {
    this.preventDefault($event)
    this.agencyPriceControl.setValue(this.component.fixedCost?.amount)
    this.isEditAgencyPrice = true
  }

  preventDefault(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
  }

  showToggleMenu() {
    if (this.scopeApprovalFlowService.isScopeEditable(this.config?.scopeVersion) || this.config?.isChangeRequest)
      return true

    if (
      this.deliverable.isPendingTrade() &&
      this.config?.scopeVersion.findDeliverableById(this.deliverable.tradedFrom.id).tradesCount > 1
    )
      return true

    if (
      this.deliverable.deliveryStatus == DeliveryStatus.IN_PROGRESS &&
      this.config?.scopeVersion.isStateApproved() &&
      this.scopeApprovalFlowService.stateChecks.deliverableStatus.isApprover &&
      this.scopeApprovalFlowService.stateChecks.deliverableStatus.isStateApproved
    )
      return (
        this.currentUser.hasPrivilege(Privilege.SCOPE_DELIVERABLE__COMPLETE) ||
        (this.currentUser.company.hasApplicationSetting(ApplicationSetting.SCOPE__DELIVERABLE__TRADING) &&
          this.currentUser.hasPrivilege(Privilege.SCOPE_DELIVERABLE__TRADE))
      )

    if (this.deliverable.isBeingTraded())
      return (
        this.currentUser.company.hasApplicationSetting(ApplicationSetting.SCOPE__DELIVERABLE__TRADING) &&
        this.currentUser.hasPrivilege(Privilege.SCOPE_DELIVERABLE__TRADE)
      )

    if (
      this.deliverable.deliveryStatus == DeliveryStatus.ON_HOLD &&
      this.scopeApprovalFlowService.stateChecks.deliverableStatus.isStateApproved &&
      this.scopeApprovalFlowService.stateChecks.deliverableStatus.isApprover
    )
      return this.currentUser.hasPrivilege(Privilege.SCOPE_DELIVERABLE__STOP)

    return false
  }

  getRiskIndicatorClass() {
    if (!this.config?.showRiskIndicator) return 'hidden'

    switch (this.component.prediction?.riskIndicator) {
      case 'LOW':
        return 'green'
      case 'MEDIUM':
        return 'yellow'
      case 'HIGH':
        return 'red'
      case 'AGENCY_DEFINED': {
        if (this.component.sourceType == 'LIBRARY') return 'black'
        if (this.component.sourceType == 'SCOPE') return 'blue'
      }
    }

    return ''
  }

  getComponentWithDescriptions(): ScopeComponent {
    return plainToInstance(ScopeComponent, {
      ...this.component,
      description: this.getFieldToDisplay('description'),
      extraDescription: this.getFieldToDisplay('extraDescription'),
      internalNote: this.getFieldToDisplay('internalNote'),
    })
  }

  getMarkup() {
    return this.component.costPlusValue && this.currentUser.company.hasApplicationSetting(ApplicationSetting.COST_PLUS)
      ? this.component.costPlusValue
      : this.component.getMarkup()
        ? this.component.getMarkup()
        : DEFAULT_PLACEHOLDER
  }

  getFieldToDisplay = (fieldName: string) => getCrFieldToDisplay<ComponentWithChanges>(fieldName, this.component)
  showDiff = (fieldName: string) => showCrDifferences<ComponentWithChanges>(this.config, this.component, fieldName)

  formatHours = (value: number): string => this.formatHoursPipe.transform(value)
  formatPrice = (value: number | Money): string => {
    const amount = value instanceof Money ? value.amount : value
    return isNil(value) ? DEFAULT_PLACEHOLDER : this.convertCurrencyPipe.transform(amount, this.config?.currency)
  }

  @HostBinding('class.change-request-removed') get isChangeRequestRemoved() {
    return isChangeItemRemoved(this.config, this.changeItem)
  }
  @HostBinding('class.change-request-added') get isChangeRequestAdded() {
    return isChangeItemAdded(this.config, this.changeItem)
  }

  protected readonly Privilege = Privilege
  protected readonly ApplicationSetting = ApplicationSetting
  protected readonly trackById = trackById
  protected readonly isPreferenceActive = isPreferenceActive
  protected readonly isNil = isNil
  protected readonly formatPercentage = formatPercentage
  protected readonly DEFAULT_PLACEHOLDER = DEFAULT_PLACEHOLDER
}
